import React, { useCallback } from 'react';
import {
  Stack,
  Title,
  Text,
  Paper,
  Group,
  Badge,
  LoadingOverlay,
  Alert,
  Divider,
  ThemeIcon,
  Anchor,
  Button,
  Box,
  SimpleGrid,
  Card
} from '@mantine/core';
import {
  IconBuildingStore,
  IconPhone,
  IconMail,
  IconWorld,
  IconClock,
  IconMapPin,
  IconAlertCircle,
  IconCategory,
  IconInfoCircle,
  IconExternalLink,
  IconSettings,
  IconCircleCheck,
  IconCircleX,
  IconTag  // SESSION_220: Alias management
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useBusinessQuery } from '../../../application/queries/businessQueries';
import { useAuth } from '../../hooks/useAuth';
import { BusinessStats } from '../../components/domain/BusinessStats';
import { BusinessMentionsList } from '../../components/domain/BusinessMentionsList';
import { CUSTOMER_ROUTES } from '../../routing/routes';

// SESSION_220: Helper to get alias management route
const getAliasManagementRoute = (): string => CUSTOMER_ROUTES.MY_BUSINESS_ALIASES;

/**
 * MyBusinessPage Component
 * SESSION_205: Redesigned with address display, improved styling
 *
 * Customer page for viewing their business profile.
 * Address correction functionality moved to Settings page.
 *
 * Features:
 * - Complete business information including address
 * - Edit Info button (navigates to Settings)
 * - Professional, polished styling
 * - Mobile-responsive layout
 *
 * Route: /my-business
 */
export const MyBusinessPage: React.FC = (): React.ReactElement => {
  const { user, role } = useAuth();
  const navigate = useNavigate();

  // Fetch business data using the user's businessId
  const {
    data: business,
    isLoading,
    error
  } = useBusinessQuery(
    user?.businessId ?? '',
    role ?? 'BusinessOwner',
    user?.businessId ?? null,
    !!user?.businessId // Only fetch if user has a business
  );

  // Handlers
  const handleEditInfo = useCallback((): void => {
    navigate(CUSTOMER_ROUTES.SETTINGS);
  }, [navigate]);

  // SESSION_220: Navigate to alias management page
  const handleManageAliases = useCallback((): void => {
    navigate(getAliasManagementRoute());
  }, [navigate]);

  // No business linked
  if (!user?.businessId) {
    return (
      <Box py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="No Business Linked"
          color="yellow"
        >
          Your account is not linked to a business yet. Please contact support.
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Box py="xl">
        <LoadingOverlay visible />
      </Box>
    );
  }

  // Error state
  if (error || !business) {
    return (
      <Box py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Business"
          color="red"
        >
          {error?.message ?? 'Could not load your business information'}
        </Alert>
      </Box>
    );
  }

  // Calculate verified and pending mentions
  const verifiedMentions = Math.floor(business.mentionCount * 0.8);
  const pendingMentions = business.mentionCount - verifiedMentions;

  // Build formatted address from available fields
  const buildFormattedAddress = (): string | null => {
    const parts: string[] = [];

    // Build street address from addrStreet (BusinessDetailDto field)
    if (business.addrStreet) parts.push(business.addrStreet);

    // Build city, state, zip line
    const cityStateZip: string[] = [];
    if (business.addrCity) cityStateZip.push(business.addrCity);
    if (business.addrState) cityStateZip.push(business.addrState);
    if (business.addrPostcode) cityStateZip.push(business.addrPostcode);

    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(', '));
    }

    // Fallback to formattedAddress if individual parts not available
    if (parts.length === 0 && business.formattedAddress) {
      return business.formattedAddress;
    }

    return parts.length > 0 ? parts.join('\n') : null;
  };

  const formattedAddress = buildFormattedAddress();

  return (
    <Box py="xl">
      <Stack gap="xl">
        {/* Header Section */}
        <Paper
          p="lg"
          radius="lg"
          style={{
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.04) 100%)',
            border: '1px solid rgba(99, 102, 241, 0.15)'
          }}
        >
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Group gap="lg">
              <ThemeIcon
                size={64}
                radius="xl"
                variant="gradient"
                gradient={{ from: 'indigo', to: 'violet', deg: 135 }}
              >
                <IconBuildingStore size={32} />
              </ThemeIcon>
              <Stack gap={6}>
                <Group gap="md" align="center">
                  <Title order={1} fw={700} style={{ letterSpacing: '-0.02em' }}>
                    {business.displayName}
                  </Title>
                  {/* SESSION_212: Improved status badge with icon */}
                  <Badge
                    size="lg"
                    radius="md"
                    variant="light"
                    color={business.isActive ? 'teal' : 'red'}
                    leftSection={
                      business.isActive
                        ? <IconCircleCheck size={14} />
                        : <IconCircleX size={14} />
                    }
                    styles={{
                      root: { fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }
                    }}
                  >
                    {business.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </Group>
                <Group gap="xs">
                  <Badge variant="light" color="gray" leftSection={<IconCategory size={12} />}>
                    {business.category}
                  </Badge>
                  {business.businessType && (
                    <Badge variant="light" color="gray">
                      {business.businessType}
                    </Badge>
                  )}
                  {business.regionName && (
                    <Badge variant="light" color="indigo" leftSection={<IconMapPin size={12} />}>
                      {business.regionName}
                    </Badge>
                  )}
                </Group>
              </Stack>
            </Group>

            {/* Action Buttons */}
            <Group gap="sm">
              {/* SESSION_220: Manage Aliases button */}
              <Button
                variant="light"
                color="violet"
                leftSection={<IconTag size={16} />}
                onClick={handleManageAliases}
              >
                Manage Aliases
              </Button>
              <Button
                variant="light"
                color="indigo"
                leftSection={<IconSettings size={16} />}
                onClick={handleEditInfo}
              >
                Edit Info
              </Button>
            </Group>
          </Group>
        </Paper>

        {/* Statistics */}
        <BusinessStats
          totalMentions={business.mentionCount}
          verifiedMentions={verifiedMentions}
          pendingMentions={pendingMentions}
          isActive={business.isActive}
        />

        {/* Business Details Grid */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          {/* Contact Information Card */}
          <Card withBorder radius="md" padding="lg" shadow="sm">
            <Group justify="space-between" mb="md">
              <Group gap="sm">
                <ThemeIcon variant="light" color="blue" size="md" radius="md">
                  <IconInfoCircle size={16} />
                </ThemeIcon>
                <Title order={4}>Contact Information</Title>
              </Group>
            </Group>

            <Stack gap="md">
              {business.phone && (
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon variant="subtle" color="gray" size="sm">
                    <IconPhone size={14} />
                  </ThemeIcon>
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={2}>
                      Phone
                    </Text>
                    <Text size="sm" fw={500}>{business.phone}</Text>
                  </Box>
                </Group>
              )}

              {business.email && (
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon variant="subtle" color="gray" size="sm">
                    <IconMail size={14} />
                  </ThemeIcon>
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={2}>
                      Email
                    </Text>
                    <Anchor href={`mailto:${business.email}`} size="sm" fw={500}>
                      {business.email}
                    </Anchor>
                  </Box>
                </Group>
              )}

              {business.website && (
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon variant="subtle" color="gray" size="sm">
                    <IconWorld size={14} />
                  </ThemeIcon>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={2}>
                      Website
                    </Text>
                    <Anchor
                      href={business.website.startsWith('http') ? business.website : `https://${business.website}`}
                      target="_blank"
                      size="sm"
                      fw={500}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {business.website}
                      </span>
                      <IconExternalLink size={12} />
                    </Anchor>
                  </Box>
                </Group>
              )}

              {business.openingHours && (
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon variant="subtle" color="gray" size="sm">
                    <IconClock size={14} />
                  </ThemeIcon>
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={2}>
                      Hours
                    </Text>
                    <Text size="sm" fw={500}>{business.openingHours}</Text>
                  </Box>
                </Group>
              )}

              {!business.phone && !business.email && !business.website && !business.openingHours && (
                <Text size="sm" c="dimmed" fs="italic">
                  No contact information on file
                </Text>
              )}
            </Stack>
          </Card>

          {/* Location Card */}
          <Card withBorder radius="md" padding="lg" shadow="sm">
            <Group justify="space-between" mb="md">
              <Group gap="sm">
                <ThemeIcon variant="light" color="orange" size="md" radius="md">
                  <IconMapPin size={16} />
                </ThemeIcon>
                <Title order={4}>Location</Title>
              </Group>
            </Group>

            <Stack gap="md">
              {formattedAddress ? (
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                    Address
                  </Text>
                  <Text size="sm" fw={500} style={{ whiteSpace: 'pre-line' }}>
                    {formattedAddress}
                  </Text>
                </Box>
              ) : (
                <Text size="sm" c="dimmed" fs="italic">
                  No address on file
                </Text>
              )}

              <Divider />

              <Group gap="sm" wrap="nowrap">
                <ThemeIcon variant="subtle" color="indigo" size="sm">
                  <IconMapPin size={14} />
                </ThemeIcon>
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={2}>
                    Service Region
                  </Text>
                  <Text size="sm" fw={500}>{business.regionName || 'No region assigned'}</Text>
                </Box>
              </Group>

              {business.subAreaName && (
                <Group gap="sm" wrap="nowrap">
                  <ThemeIcon variant="subtle" color="indigo" size="sm">
                    <IconMapPin size={14} />
                  </ThemeIcon>
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={2}>
                      Sub-Area
                    </Text>
                    <Text size="sm" fw={500}>{business.subAreaName}</Text>
                  </Box>
                </Group>
              )}
            </Stack>
          </Card>
        </SimpleGrid>

        {/* Description Section (Full Width) */}
        {business.description && (
          <Card withBorder radius="md" padding="lg" shadow="sm">
            <Group gap="sm" mb="md">
              <ThemeIcon variant="light" color="violet" size="md" radius="md">
                <IconInfoCircle size={16} />
              </ThemeIcon>
              <Title order={4}>About</Title>
            </Group>
            <Text size="sm" lh={1.6} style={{ color: 'var(--mantine-color-gray-7)' }}>
              {business.description}
            </Text>
          </Card>
        )}

        {/* Associated Mentions - Only show for Admin users, BusinessOwners use My Mentions page */}
        {role === 'Admin' && (
          <BusinessMentionsList
            businessId={business.id}
            businessName={business.displayName}
          />
        )}
      </Stack>
    </Box>
  );
};
